import { ref, computed } from "vue";
import { apiClient } from "@/services/api";
import authStore from "@/store/auth";
import { loadCoverLetterDetails, saveCoverLetterDetails } from "@/utils/coverLetterDetails";

// Bracketed blanks the AI leaves for details it can't know, e.g. [YOUR PORTFOLIO LINK].
const PLACEHOLDER_PATTERN = "\\[([A-Z][A-Z0-9 /_.-]+)\\]";

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const extractPlaceholders = (text) => {
  const found = new Set();
  const re = new RegExp(PLACEHOLDER_PATTERN, "g");
  let m;
  while ((m = re.exec(text || "")) !== null) found.add(m[1]);
  return [...found];
};

/**
 * Fill-in helper for the blanks in a cover letter.
 *
 * Each new draft goes through `prepareDraft`: blanks the user answered before
 * (saved in this browser) are filled automatically, and an input appears for
 * each one still open. Typing an answer replaces the blank everywhere in the
 * letter through `setContent`. Answers the user typed are saved to their
 * profile with `saveTypedToServer` after they apply.
 *
 * @param {object} opts
 * @param {import('vue').Ref|Function} opts.jobId  current job id (for AI suggestions)
 * @param {() => string} opts.getContent           current letter text
 * @param {(text: string) => void} opts.setContent write the filled letter back
 */
export function useCoverLetterPlaceholders({ jobId, getContent, setContent }) {
  const answers = ref({});
  const suggesting = ref({});
  // The draft with its blanks intact; answers are always re-applied to this,
  // so they can be edited in any order and cleared again.
  const base = ref("");
  // What the user typed (not auto-filled or AI-suggested); only these are
  // saved to the profile. Kept across revisions: once an answer is remembered
  // it fills the blank by itself, but it still counts as typed.
  const typedAnswers = ref({});

  const keys = computed(() => Object.keys(answers.value));
  const userId = () => authStore.state.user?.id ?? null;
  const currentJobId = () => (typeof jobId === "function" ? jobId() : jobId?.value);

  const fill = (text, values) => {
    let result = text;
    for (const [k, v] of Object.entries(values)) {
      const re = new RegExp(`\\[${escapeRegExp(k)}\\]`, "g");
      result = result.replace(re, (v || "").trim() || `[${k}]`);
    }
    return result;
  };

  /** Take a new draft, fill what we can, and return the filled text. */
  const prepareDraft = (text) => {
    const saved = loadCoverLetterDetails(userId()) || {};
    const autoFilled = (text || "").replace(new RegExp(PLACEHOLDER_PATTERN, "g"), (match, key) => saved[key] || match);
    const open = extractPlaceholders(autoFilled);
    const next = {};
    // Keep anything already typed for a blank that's still in the new draft.
    for (const key of open) next[key] = answers.value[key] ?? saved[key] ?? "";
    answers.value = next;
    base.value = autoFilled;
    return fill(autoFilled, next);
  };

  const applyAnswer = (key, value) => {
    answers.value = { ...answers.value, [key]: value };
    setContent(fill(base.value || getContent(), answers.value));
    if ((value || "").trim()) saveCoverLetterDetails(userId(), { [key]: value.trim() });
  };

  const onPlaceholderTyped = (key, value) => {
    typedAnswers.value = { ...typedAnswers.value, [key]: value };
    applyAnswer(key, value);
  };

  const suggestPlaceholder = async (key) => {
    if (suggesting.value[key]) return;
    suggesting.value = { ...suggesting.value, [key]: true };
    try {
      const res = await apiClient.post("/cover-letters/suggest-placeholder/", {
        placeholder_key: key,
        job_id: currentJobId(),
        cover_letter_content: getContent(),
      });
      const suggestion = res.data?.suggestion || "";
      if (suggestion) applyAnswer(key, suggestion);
    } catch (e) {
      console.error("AI placeholder suggestion failed:", e);
    } finally {
      suggesting.value = { ...suggesting.value, [key]: false };
    }
  };

  const saveTypedToServer = async () => {
    if (!authStore.state.isLoggedIn) return;
    const typed = {};
    for (const [key, raw] of Object.entries(typedAnswers.value)) {
      const value = (raw || "").trim();
      if (value) typed[key] = value;
    }
    if (!Object.keys(typed).length) return;
    try {
      await apiClient.post("/profile/save-placeholder-details/", { answers: typed });
    } catch (e) {
      // silent: the browser copy is still saved
    }
  };

  const reset = () => {
    answers.value = {};
    suggesting.value = {};
    base.value = "";
    typedAnswers.value = {};
  };

  return {
    placeholderAnswers: answers,
    placeholderKeys: keys,
    suggestingPlaceholder: suggesting,
    prepareDraft,
    onPlaceholderTyped,
    suggestPlaceholder,
    saveTypedToServer,
    reset,
  };
}
